/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.init();
      this.bindEvents();
      this.initUpsellProgress();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.teardownUpsellInteractions();
      window.removeEventListener('pageshow', CartDrawer.handlePageshow);
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);

      window.addEventListener('pageshow', CartDrawer.handlePageshow);
    }

    teardownUpsellInteractions() {
      if (this.upsellSlider && this.upsellProgressHandler) {
        this.upsellSlider.removeEventListener('scroll', this.upsellProgressHandler);
      }

      if (this.upsellResizeHandler) {
        window.removeEventListener('resize', this.upsellResizeHandler);
      }

      if (this.upsellSlider && this.upsellPointerDownHandler) {
        this.upsellSlider.removeEventListener('pointerdown', this.upsellPointerDownHandler);
      }

      if (this.upsellSlider && this.upsellPointerMoveHandler) {
        this.upsellSlider.removeEventListener('pointermove', this.upsellPointerMoveHandler);
      }

      if (this.upsellSlider && this.upsellPointerUpHandler) {
        this.upsellSlider.removeEventListener('pointerup', this.upsellPointerUpHandler);
        this.upsellSlider.removeEventListener('pointercancel', this.upsellPointerUpHandler);
        this.upsellSlider.removeEventListener('lostpointercapture', this.upsellPointerUpHandler);
      }

      this.isUpsellDragging = false;
      this.upsellSlider = null;
    }

    initUpsellProgress() {
      this.teardownUpsellInteractions();

      const slider = this.querySelector('#cart-drawer-recommendations .cart-drawer-upsells');
      const fill = this.querySelector('.cart-drawer-upsell-progress__fill');
      const progress = this.querySelector('.cart-drawer-upsell-progress');

      if (!slider || !fill || !progress) return;

      this.upsellSlider = slider;

      const updateProgress = () => {
        if (window.innerWidth >= 768) {
          progress.style.display = 'none';
          fill.style.width = '0%';
          fill.style.left = '0%';
          return;
        }

        progress.style.display = 'block';

        const maxScroll = Math.max(slider.scrollWidth - slider.clientWidth, 0);

        if (maxScroll <= 1) {
          fill.style.width = '100%';
          fill.style.left = '0%';
          return;
        }

        const visibleRatio = slider.clientWidth / slider.scrollWidth;
        const thumbWidth = Math.min(Math.max(visibleRatio * 100, 20), 100);
        const travel = 100 - thumbWidth;
        const scrollLeft = Math.min(Math.max(slider.scrollLeft, 0), maxScroll);

        let left = (scrollLeft / maxScroll) * travel;

        if (scrollLeft >= maxScroll - 1) {
          left = travel;
        }

        fill.style.width = `${thumbWidth}%`;
        fill.style.left = `${left}%`;
      };

      this.upsellProgressHandler = () => {
        requestAnimationFrame(updateProgress);
      };

      this.upsellResizeHandler = () => {
        requestAnimationFrame(updateProgress);
      };

      this.upsellPointerDownHandler = (evt) => {
        if (window.innerWidth >= 768) return;
        if (evt.pointerType !== 'mouse') return;
        if (evt.button !== 0) return;

        this.isUpsellDragging = true;
        this.upsellStartX = evt.clientX;
        this.upsellStartScrollLeft = slider.scrollLeft;
        this.upsellPointerId = evt.pointerId;

        slider.classList.add('is-dragging');

        if (slider.setPointerCapture) {
          slider.setPointerCapture(evt.pointerId);
        }
      };

      this.upsellPointerMoveHandler = (evt) => {
        if (!this.isUpsellDragging || evt.pointerId !== this.upsellPointerId) return;
        if (window.innerWidth >= 768) return;
        if (evt.pointerType !== 'mouse') return;

        const deltaX = evt.clientX - this.upsellStartX;
        slider.scrollLeft = this.upsellStartScrollLeft - deltaX;
      };

      this.upsellPointerUpHandler = (evt) => {
        if (evt.pointerType && evt.pointerType !== 'mouse') return;
        if (evt.pointerId !== this.upsellPointerId) return;

        this.isUpsellDragging = false;
        slider.classList.remove('is-dragging');

        if (slider.releasePointerCapture && slider.hasPointerCapture && slider.hasPointerCapture(evt.pointerId)) {
          slider.releasePointerCapture(evt.pointerId);
        }

        requestAnimationFrame(updateProgress);
      };

      slider.addEventListener('scroll', this.upsellProgressHandler, { passive: true });
      window.addEventListener('resize', this.upsellResizeHandler);

      slider.addEventListener('pointerdown', this.upsellPointerDownHandler);
      slider.addEventListener('pointermove', this.upsellPointerMoveHandler);
      slider.addEventListener('pointerup', this.upsellPointerUpHandler);
      slider.addEventListener('pointercancel', this.upsellPointerUpHandler);
      slider.addEventListener('lostpointercapture', this.upsellPointerUpHandler);

      updateProgress();
      requestAnimationFrame(updateProgress);
      setTimeout(updateProgress, 60);
      setTimeout(updateProgress, 180);
    }

    static handlePageshow(evt) {
      if (evt.persisted) {
        document.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true, cancelable: false })
        );
      }
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');

      if (quickAddDrawer) {
        quickAddDrawer.close();
      }

      super.open(opener, elementToFocus);
      this.initUpsellProgress();
    }

    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      }
    }

    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      this.initUpsellProgress();
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
